import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const NotificationSettingsModal = ({ isOpen, onClose }) => {
  console.log('[NotificationSettingsModal] render, isOpen:', isOpen);
  const { instance, accounts } = useMsal();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'get', '/notification-preferences');
      setFeatures(response.data.features || []);
    } catch (err) {
      const detail = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Failed to load notification preferences: ${detail}`);
      console.error('NotificationSettings fetch error:', err.response?.status, err.response?.data, err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (isOpen) fetchPreferences();
  }, [isOpen, fetchPreferences]);

  const handleToggle = async (feature, currentSubscribed) => {
    const newValue = !currentSubscribed;
    setSaving(feature);
    try {
      await authenticatedApiCall(instance, accounts, 'put', '/notification-preferences', {
        feature,
        subscribed: newValue
      });
      setFeatures(prev => prev.map(f => f.feature === feature ? { ...f, subscribed: newValue } : f));
    } catch (err) {
      console.error('Failed to update preference:', err);
    } finally {
      setSaving(null);
    }
  };

  if (!isOpen) return null;

  const cronFeatures = features.filter(f => f.trigger === 'cron');
  const eventFeatures = features.filter(f => f.trigger === 'event');

  const FeatureRow = ({ f }) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
      opacity: saving === f.feature ? 0.6 : 1,
      transition: 'opacity 0.15s'
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: '14px', color: '#2c3e50' }}>{f.label}</div>
        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>{f.description}</div>
        <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '2px' }}>{f.schedule}</div>
      </div>
      <div style={{ marginLeft: '16px', flexShrink: 0 }}>
        {f.canUnsubscribe ? (
          <button
            onClick={() => handleToggle(f.feature, f.subscribed)}
            disabled={saving === f.feature}
            title={f.subscribed ? 'Click to opt out' : 'Click to opt in'}
            style={{
              width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
              backgroundColor: f.subscribed ? '#28a745' : '#ccc',
              position: 'relative', transition: 'background-color 0.2s',
              padding: 0
            }}
          >
            <div style={{
              width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'white',
              position: 'absolute', top: '2px',
              left: f.subscribed ? '24px' : '2px',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }} />
          </button>
        ) : (
          <span style={{
            fontSize: '11px', color: '#6c757d', padding: '4px 8px',
            backgroundColor: '#f0f0f0', borderRadius: '4px', whiteSpace: 'nowrap'
          }}>Always on</span>
        )}
      </div>
    </div>
  );

  const SectionHeader = ({ title }) => (
    <div style={{
      padding: '10px 16px', backgroundColor: '#f8f9fa',
      fontSize: '12px', fontWeight: 600, color: '#495057',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      borderBottom: '1px solid #e9ecef'
    }}>
      {title}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', width: '520px',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e9ecef',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#2c3e50' }}>Notification Settings</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6c757d' }}>
              Manage which email notifications you receive
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer',
            color: '#6c757d', padding: '0 4px', lineHeight: 1
          }}>&times;</button>
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>Loading...</div>
          ) : error ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#dc3545' }}>{error}</div>
          ) : (
            <>
              {cronFeatures.length > 0 && (
                <>
                  <SectionHeader title="Scheduled Reports" />
                  {cronFeatures.map(f => <FeatureRow key={f.feature} f={f} />)}
                </>
              )}
              {eventFeatures.length > 0 && (
                <>
                  <SectionHeader title="Event Notifications" />
                  {eventFeatures.map(f => <FeatureRow key={f.feature} f={f} />)}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #e9ecef',
          display: 'flex', justifyContent: 'flex-end'
        }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', backgroundColor: '#6c757d', color: 'white',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
          }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
